import React, { useState } from 'react';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'hooks/use-user';
import { tracker } from 'utils/analytics';

import { CompleteAccount } from 'src/components/modals/complete-account/complete-account';
import { EmailEntryForm } from './email-entry-form';
import { CodeVerificationForm } from './code-verification-form';

export type LoginStep = 'code' | 'email' | 'profile';

export type EmailCodeLoginFormProps = {
  onClose?: (success?: boolean) => void;
  analyticsEventLabel?: string | null;
  onLoginSuccess?: () => void;
  onBack?: () => void;
  publicContextSessionId?: string | null;
  onStepChange?: (step: LoginStep) => void;
  dispensaryId?: string;
  showMedicalCardForm?: boolean;
};

/**
 * Tracks account login event for analytics
 */
const trackAccountLogin = (
  dispensary: { id?: string; name?: string } | null,
  userId: string | undefined,
  analyticsEventLabel: string | null,
  isNewUser: boolean
): void => {
  tracker.setContext({ activeDispensary: dispensary as any });
  tracker.accountLogin({
    newAccount: isNewUser,
    customerId: userId ?? '',
    dispensary: {
      id: dispensary?.id ?? '',
      name: dispensary?.name ?? '',
    },
    analyticsEventLabel: analyticsEventLabel ?? undefined,
  });
};

/**
 * Email Code Login Form - Main container component for passwordless email authentication.
 * Manages a multi-step flow:
 * 1. Email entry - User enters their email address
 * 2. Code verification - User enters the 6-digit code sent to their email
 * 3. Profile creation (new users only) - User completes their profile with name, phone, and birthday
 */
export function EmailCodeLoginForm({
  onClose,
  analyticsEventLabel = null,
  onLoginSuccess,
  onBack,
  publicContextSessionId = null,
  onStepChange,
  dispensaryId,
  showMedicalCardForm = false,
}: EmailCodeLoginFormProps): JSX.Element {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');

  /**
   * Updates the step and notifies parent component
   */
  const updateStep = (newStep: LoginStep): void => {
    setStep(newStep);
    onStepChange?.(newStep);
  };

  const { query = {} } = useRouter() ?? {};
  const { dispensary } = useDispensary();
  const user = useUser();

  // Get public context session ID from query if not provided as prop
  const sessionId =
    publicContextSessionId ??
    (Array.isArray(query['session-id']) ? query['session-id'][0] : query['session-id'] ?? null);

  /**
   * Handles successful code send - transitions to code entry step
   */
  const handleCodeSent = (submittedEmail: string): void => {
    setEmail(submittedEmail);
    updateStep('code');
  };

  /**
   * Handles going back to email entry step
   */
  const handleBackToEmail = (): void => {
    updateStep('email');
  };

  /**
   * Handles successful code verification.
   * If user is new, transitions to profile creation step.
   * Otherwise, completes the login flow.
   */
  const handleCodeVerificationSuccess = (isNewUser: boolean): void => {
    if (isNewUser) {
      updateStep('profile');
    } else {
      trackAccountLogin(dispensary, user.id, analyticsEventLabel, false);
      onLoginSuccess?.();
      onClose?.(true);
    }
  };

  /**
   * Handles successful profile creation for new users
   */
  const handleProfileSuccess = (): void => {
    trackAccountLogin(dispensary, user.id, analyticsEventLabel, true);
    onLoginSuccess?.();
    onClose?.(true);
  };

  if (step === 'profile') {
    return (
      <ProfileStepContainer>
        <CompleteAccount close={handleProfileSuccess} showMedicalCardForm={showMedicalCardForm} />
      </ProfileStepContainer>
    );
  }

  if (step === 'code') {
    return (
      <CodeVerificationForm
        email={email}
        onSuccess={handleCodeVerificationSuccess}
        onBack={handleBackToEmail}
        publicContextSessionId={sessionId}
        dispensaryId={dispensaryId}
      />
    );
  }

  return <EmailEntryForm onCodeSent={handleCodeSent} onBack={onBack} dispensaryId={dispensaryId} />;
}

const ProfileStepContainer = styled.div`
  padding-top: 40px;
`;

export default EmailCodeLoginForm;
